/* eslint-disable import/first */
// Polyfill AudioContext for browsers that don't support it (prevents react-mic crash)
// This must run before any imports that use react-mic
if (typeof window !== 'undefined' && !window.AudioContext && !window.webkitAudioContext) {
  console.warn('AudioContext not supported - audio recording will be unavailable');
  // Provide a stub that won't crash on instantiation
  window.AudioContext = class AudioContextStub {
    constructor() {
      this.state = 'suspended';
      this.destination = {};
      this.sampleRate = 44100;
    }
    createMediaStreamSource() { return { connect: () => {} }; }
    createAnalyser() { return { connect: () => {}, fftSize: 2048, frequencyBinCount: 1024, getByteTimeDomainData: () => {} }; }
    createScriptProcessor() { return { connect: () => {}, disconnect: () => {} }; }
    close() { return Promise.resolve(); }
    resume() { return Promise.resolve(); }
    suspend() { return Promise.resolve(); }
  };
}

import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './utilities/i18next'; // Initialize i18n before any components - must be early
import App from './App';
import TranscriptionTester from './pages/TranscriptionTester';
import reportWebVitals from './reportWebVitals';
import { SharedStateProvider } from './hooks/SharedStateContext';
import { TimerProvider } from './hooks/TimerContext';

import { PostHogProvider} from 'posthog-js/react'
import { _config } from './Constants';
import * as Sentry from "@sentry/react";

// TOOLS
const options = { api_host: _config.posthogHost }

const root = ReactDOM.createRoot(document.getElementById('root'));

const pathname = window.location.pathname.replace(/\/+$/, '') || '/';

// Reload once if a chunk fails to load — typically caused by a stale HTML
// referencing chunk hashes that no longer exist after a fresh deploy.
const lazyWithRetry = (componentImport) =>
  React.lazy(async () => {
    const hasRefreshed = window.sessionStorage.getItem('chunk-refreshed') === 'true';
    try {
      const component = await componentImport();
      window.sessionStorage.setItem('chunk-refreshed', 'false');
      return component;
    } catch (error) {
      const isChunkError =
        error?.name === 'ChunkLoadError' ||
        /Loading (CSS )?chunk [^\s]+ failed/i.test(error?.message || '');
      if (isChunkError && !hasRefreshed) {
        window.sessionStorage.setItem('chunk-refreshed', 'true');
        window.location.reload();
        return { default: () => null };
      }
      throw error;
    }
  });

// NOTE: Onboarding redirect logic has been moved to App.js (onAuthSuccess callback)
// This ensures users authenticate BEFORE being redirected to onboarding.
// The flow is now: App.js shows AuthPage → user logs in → check onboarding needs → redirect if needed

// Determine which component to render based on path
const AppComponent = pathname === '/lite'
  ? lazyWithRetry(() => import('./lite/LiteApp'))
  : pathname.startsWith('/activity/')
    ? lazyWithRetry(() => import('./activity/ActivityApp'))
    : pathname === '/onboarding'
      ? lazyWithRetry(() => import('./pages/OnboardingPage'))
      : pathname === '/speech-profile'
        ? lazyWithRetry(() => import('./pages/SpeechProfilePage'))
        : (pathname === '/transcription-tester' || pathname.startsWith('/transcription-tester'))
          ? TranscriptionTester
          : App;

root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>} showDialog>
      <PostHogProvider
        apiKey={_config.posthogKey}
        options={options}
      >
        <SharedStateProvider>
          <TimerProvider>
            <React.Suspense fallback={
              <div className="loader-container">
                <div className="loader"></div>
              </div>
            }>
              <AppComponent />
            </React.Suspense>
          </TimerProvider>
        </SharedStateProvider>
      </PostHogProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
